import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import Conversation from "@/model/interface/Conversation";
import {
  ComposeDraft,
  EMPTY_COMPOSE_DRAFT,
  EMPTY_MESSAGE_DRAFT,
  MessageDraft,
} from "@/model/interface/MessageDraft";

interface MessageDialogContextValue {
  isMessageDialogOpen: boolean;
  openRequestId: number;
  triggerPosition: { x: number; y: number } | null;
  openMessageDialog: (triggerPos?: { x: number; y: number }) => void;
  closeMessageDialog: () => void;
  requestCloseMessageDialog: () => void;
  registerCloseHandler: (fn: (() => void) | null) => void;
  toggleMessageDialog: () => void;
  pendingRecipient: string | null;
  setPendingRecipient: (recipient: string | null) => void;
  activeConversation: Conversation | null;
  setActiveConversation: (conversation: Conversation | null) => void;
  isComposing: boolean;
  setIsComposing: (composing: boolean) => void;
  composeDraft: ComposeDraft;
  setComposeDraft: React.Dispatch<React.SetStateAction<ComposeDraft>>;
  threadDraft: (conversationId: string) => MessageDraft;
  setThreadDraft: (conversationId: string, update: (current: MessageDraft) => MessageDraft) => void;
}

const MessageDialogContext = createContext<MessageDialogContextValue | null>(null);

export function MessageDialogProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    return sessionStorage.getItem("messageDialogOpen") === "true";
  });
  const [pendingRecipient, setPendingRecipient] = useState<string | null>(null);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);

  // Drafts and the compose view live here so dismissing the card keeps whatever was half-written.
  const [isComposing, setIsComposing] = useState(false);
  const [composeDraft, setComposeDraft] = useState<ComposeDraft>(EMPTY_COMPOSE_DRAFT);
  const [threadDrafts, setThreadDrafts] = useState<Record<string, MessageDraft>>({});

  const threadDraft = useCallback(
    (conversationId: string) => threadDrafts[conversationId] ?? EMPTY_MESSAGE_DRAFT,
    [threadDrafts],
  );

  const setThreadDraft = useCallback((conversationId: string, update: (current: MessageDraft) => MessageDraft) => {
    setThreadDrafts((current) => ({
      ...current,
      [conversationId]: update(current[conversationId] ?? EMPTY_MESSAGE_DRAFT),
    }));
  }, []);

  const closeHandlerRef = useRef<(() => void) | null>(null);
  const registerCloseHandler = useCallback((fn: (() => void) | null) => {
    closeHandlerRef.current = fn;
  }, []);

  const [triggerPosition, setTriggerPosition] = useState<{ x: number; y: number } | null>(null);

  const [openRequestId, setOpenRequestId] = useState(0);

  const openMessageDialog = useCallback((triggerPos?: { x: number; y: number }) => {
    if (triggerPos) setTriggerPosition(triggerPos);
    setIsOpen(true);
    setOpenRequestId((current) => current + 1);
    sessionStorage.setItem("messageDialogOpen", "true");
  }, []);

  const closeMessageDialog = useCallback(() => {
    setIsOpen(false);
    sessionStorage.setItem("messageDialogOpen", "false");
  }, []);

  const requestCloseMessageDialog = useCallback(() => {
    if (closeHandlerRef.current) {
      closeHandlerRef.current();
    } else {
      closeMessageDialog();
    }
  }, [closeMessageDialog]);

  const toggleMessageDialog = useCallback(() => {
    if (isOpen) {
      requestCloseMessageDialog();
    } else {
      openMessageDialog();
    }
  }, [isOpen, requestCloseMessageDialog, openMessageDialog]);

  return (
    <MessageDialogContext.Provider
      value={{
        isMessageDialogOpen: isOpen,
        openRequestId,
        triggerPosition,
        openMessageDialog,
        closeMessageDialog,
        requestCloseMessageDialog,
        registerCloseHandler,
        toggleMessageDialog,
        pendingRecipient,
        setPendingRecipient,
        activeConversation,
        setActiveConversation,
        isComposing,
        setIsComposing,
        composeDraft,
        setComposeDraft,
        threadDraft,
        setThreadDraft,
      }}
    >
      {children}
    </MessageDialogContext.Provider>
  );
}

export function useMessageDialog() {
  const context = useContext(MessageDialogContext);
  if (!context) {
    throw new Error("useMessageDialog must be used within a MessageDialogProvider");
  }
  return context;
}
